import { Link, useNavigate } from "react-router-dom";
import { Trash, Minus, Plus, ArrowRight } from "@phosphor-icons/react";
import { formatPrice, tierPrice } from "../lib/api";
import { useCart } from "../context/CartContext";

export default function CartPage() {
  const { items, updateQty, removeItem, subtotal, vat, total } = useCart();
  const navigate = useNavigate();

  if (items.length === 0) {
    return (
      <div className="mx-auto max-w-3xl px-4 py-24 text-center">
        <h1 className="font-heading text-3xl font-black uppercase tracking-tighter">Grozs ir tukšs</h1>
        <Link to="/katalogs" data-testid="empty-cart-catalog-link" className="btn-industrial mt-6 inline-block bg-[#2E86D1] px-6 py-3 text-sm font-semibold tracking-wide text-white hover:bg-[#1F6DB2]">Doties uz katalogu</Link>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-7xl px-4 py-8">
      <h1 className="font-heading text-3xl md:text-4xl font-bold tracking-tight">Grozs</h1>
      <div className="mt-6 grid grid-cols-1 lg:grid-cols-[1fr_340px] gap-8 items-start">
        <div className="rounded-2xl overflow-hidden border border-border">
          <div className="hidden md:grid grid-cols-[1fr_140px_120px_110px_40px] gap-3 bg-[#1D1D1F] text-white px-4 py-2.5 text-xs font-semibold tracking-wide">
            <span>Produkts</span><span className="text-center">Daudzums</span><span className="text-right">Cena/gab</span><span className="text-right">Summa</span><span />
          </div>
          {items.map((item) => {
            const unit = tierPrice(item.price_tiers, item.qty);
            const nextTier = [...item.price_tiers].sort((a, b) => a.min_qty - b.min_qty).find((t) => t.min_qty > item.qty);
            return (
              <div key={item.sku} data-testid={`cart-row-${item.sku}`} className="grid grid-cols-1 md:grid-cols-[1fr_140px_120px_110px_40px] gap-3 items-center border-b border-border last:border-b-0 px-4 py-4">
                <div className="flex items-center gap-3">
                  <img src={item.image} alt={item.name} className="h-14 w-14 object-cover border border-border shrink-0" />
                  <div className="min-w-0">
                    <Link to={`/produkts/${item.slug}`} className="text-sm font-semibold hover:text-[#2E86D1] line-clamp-2">{item.name}</Link>
                    <p className="text-xs text-slate-500">Art. {item.sku}</p>
                    {nextTier && (
                      <p className="text-[11px] text-[#16A34A] font-semibold">Pērkot no {nextTier.min_qty} gab — {formatPrice(nextTier.price)}/gab</p>
                    )}
                  </div>
                </div>
                <div className="flex items-center border border-slate-300 w-fit md:mx-auto">
                  <button data-testid={`cart-minus-${item.sku}`} aria-label="Samazināt" onClick={() => updateQty(item.sku, item.qty - 1)} className="h-9 w-9 flex items-center justify-center hover:bg-[#2E86D1] hover:text-white hover:border-[#2E86D1] btn-industrial"><Minus size={14} /></button>
                  <input data-testid={`cart-qty-${item.sku}`} type="number" min="1" value={item.qty} onChange={(e) => updateQty(item.sku, Math.max(1, parseInt(e.target.value) || 1))} className="h-9 w-14 text-center text-sm font-bold focus:outline-none" />
                  <button data-testid={`cart-plus-${item.sku}`} aria-label="Palielināt" onClick={() => updateQty(item.sku, item.qty + 1)} className="h-9 w-9 flex items-center justify-center hover:bg-[#2E86D1] hover:text-white hover:border-[#2E86D1] btn-industrial"><Plus size={14} /></button>
                </div>
                <p className="text-sm md:text-right">{formatPrice(unit)}</p>
                <p className="text-sm font-bold md:text-right">{formatPrice(unit * item.qty)}</p>
                <button data-testid={`cart-remove-${item.sku}`} aria-label="Noņemt" onClick={() => removeItem(item.sku)} className="text-slate-400 hover:text-[#2E86D1] justify-self-start md:justify-self-end"><Trash size={18} /></button>
              </div>
            );
          })}
        </div>

        <div className="rounded-2xl border border-slate-300 p-6 sticky top-24">
          <h2 className="font-heading text-lg font-semibold tracking-tight">Kopsavilkums</h2>
          <div className="mt-4 space-y-2 text-sm">
            <div className="flex justify-between"><span>Summa bez PVN</span><span data-testid="cart-subtotal" className="font-semibold">{formatPrice(subtotal)}</span></div>
            <div className="flex justify-between"><span>PVN 21%</span><span>{formatPrice(vat)}</span></div>
            <div className="flex justify-between border-t border-slate-300 pt-3 text-base font-black font-heading"><span>KOPĀ</span><span data-testid="cart-total">{formatPrice(total)}</span></div>
          </div>
          <button data-testid="checkout-btn" onClick={() => navigate("/noformesana")}
            className="btn-industrial mt-6 w-full flex items-center justify-center gap-2 bg-[#2E86D1] py-3.5 text-sm font-semibold tracking-wide text-white hover:bg-[#1F6DB2]">
            Noformēt pasūtījumu <ArrowRight size={18} />
          </button>
          <Link to="/piedavajums" data-testid="cart-quote-link" className="mt-3 block text-center text-xs font-semibold text-slate-500 hover:text-[#2E86D1]">
            Liels apjoms? Pieprasiet individuālu piedāvājumu →
          </Link>
        </div>
      </div>
    </div>
  );
}
